import React from 'react';
import {
  Typography,
  TextField,
  Grid,
  Paper,
  Divider,
  Button,
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import { ApiService } from '../../hooks';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import { useTranslation } from 'react-i18next';
import {
  HealthSystemDomainFeedFeedSuppliersFeedSupplierViewModel,
  HealthSystemDomainSlaughterPreparationsSlaughterPreDeliveryCommand
} from '../../api/HealthSystemApi';
import FeedSupplierAutocomplete from './components/FeedSupplierAutocomplete';
import { FoodChainQuestionListRef } from './components/FoodChainQuestion';
import FoodChainQuestion from './components/FoodChainQuestion';
import { notifications } from '../../utils';

type Supplier = {
  ubn: string;
  name: string;
  address: string;
  country: string;
};

const emptySupplier: Supplier = {
  ubn: '',
  name: '',
  address: '',
  country: ''
};

type PreDeliveryDeclarationFormProps = {
  onSaveSuccess?: () => void;
};

const PreDeliveryDeclarationForm: React.FC<PreDeliveryDeclarationFormProps> = ({ onSaveSuccess }) => {
  const [errors, setErrors] = React.useState({ amountOfPigs: '', checklist: '', feedSuppliers: '' });
  const [isCompleted, setIsCompleted] = React.useState(false);
  const [amountOfPigs, setAmountOfPigs] = React.useState<number>(0);
  const [eligibleAnimalsCount, setEligibleAnimalsCount] = React.useState<number | null>(null);
  const [answers, setAnswers] = React.useState<Record<string, boolean | null>>({});
  const [pigSuppliers, setPigSuppliers] = React.useState<Supplier[]>([emptySupplier]);
  const [feedSuppliers, setFeedSuppliers] = React.useState<Supplier[]>([emptySupplier]);
  const [veterinaryInfo, setVeterinaryInfo] = React.useState<{ id: string; name: string; address: string }>();
  const [warnings, setWarnings] = React.useState<{ amountOfPigs?: string }>({});
  const { t } = useTranslation();
  const apiService = new ApiService();
  const currentDate = new Date();
  const foodChainRef = React.useRef<FoodChainQuestionListRef>(null);

  const getSummaryInfo = async () => {
    try {
      const result = await apiService.api.slaughterPreparation.slaughterPreparationGetSummaryInfo();
      setVeterinaryInfo({
        id: result.data.veterinaryId ?? '',
        name: result.data.veterinaryPracticeName ?? '',
        address: result.data.veterinaryAdress ?? ''
      });

      setEligibleAnimalsCount(result.data.eligibleAnimalsCount);

      setPigSuppliers((prev) => {
        const newList = [
          emptySupplier,
          ...(result.data.previousPigletsSuppliers ?? []).map((dto) => ({
            ubn: dto.ubn ?? '',
            name: dto.name ?? '',
            address: dto.address ?? '',
            country: dto.country ?? ''
          }))
        ];
        return JSON.stringify(prev) === JSON.stringify(newList) ? prev : newList;
      });

      setFeedSuppliers((prev) => {
        const newList = [
          emptySupplier,
          ...(result.data.previousFeedSuppliers ?? []).map((dto) => ({
            ubn: dto.ubn ?? '',
            name: dto.name ?? '',
            address: dto.address ?? '',
            country: dto.country ?? ''
          }))
        ];
        return JSON.stringify(prev) === JSON.stringify(newList) ? prev : newList;
      });
    } catch (e) {
      notifications.error(t('f-pre-delivery-summary-info-error', { error: (e as Error).toString() }));
    }
  };

  React.useEffect(() => {
    if (!eligibleAnimalsCount) getSummaryInfo();
  }, []);

  const handleFeedSupplierSelect = (supplier: HealthSystemDomainFeedFeedSuppliersFeedSupplierViewModel) => {
    const mappedSupplier: Supplier = {
      ubn: supplier.registrationNumber ?? '',
      name: supplier.companyName ?? '',
      address: supplier.companyAddress ?? '',
      country: supplier.country ?? ''
    };

    setFeedSuppliers((prev) => [...prev, mappedSupplier]);
  };

  const handlePigSupplierChange = (index: number, key: keyof Supplier, value: string) => {
    setPigSuppliers((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [key]: value };
      return updated;
    });
  };

  const removePigSupplier = (index: number) => {
    setPigSuppliers((prev) => prev.filter((_, i) => i !== index));
  };

  const removeFeedSupplier = (index: number) => {
    setFeedSuppliers((prev) => prev.filter((_, i) => i !== index));
  };

  const handleAmountChange = (rawValue: string) => {
    const parsed = parseInt(rawValue);
    const value = isNaN(parsed) || parsed < 0 ? 0 : parsed;

    setAmountOfPigs(rawValue === '' ? 0 : value);

    if (parsed < 0) {
      setErrors((prev) => ({
        ...prev,
        amountOfPigs: 'Negative values are not allowed.'
      }));
      return;
    }

    if (eligibleAnimalsCount !== null && value > eligibleAnimalsCount) {
      setWarnings((prev) => ({
        ...prev,
        amountOfPigs: `You entered more than the available (${eligibleAnimalsCount}) pigs.`
      }));
    } else {
      setErrors((prev) => ({ ...prev, amountOfPigs: '' }));
    }
  };

  const renderSuppliersTable = (suppliers: Supplier[], onRemove?: (index: number) => void) => {
    if (suppliers.length === 0) return null;

    return (
      <>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>{t('f-pre-delivery-ubn')}</strong>
              </TableCell>
              <TableCell>
                <strong>{t('f-pre-delivery-company-name')}</strong>
              </TableCell>
              <TableCell>
                <strong>{t('f-pre-delivery-address')}</strong>
              </TableCell>
              <TableCell>
                <strong>{t('f-country')}</strong>
              </TableCell>
              {!isCompleted && (
                <TableCell align="right">
                  <strong>Actions</strong>
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {suppliers.map((item, index) => (
              <TableRow key={index}>
                <TableCell>{item.ubn}</TableCell>
                <TableCell>{item.name}</TableCell>
                <TableCell>{item.address}</TableCell>
                <TableCell>{item.country}</TableCell>
                {!isCompleted && (
                  <TableCell align="right">
                    {onRemove && (
                      <IconButton color="error" onClick={() => onRemove(index)}>
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </>
    );
  };

  const validateForm = () => {
    const newErrors = {
      amountOfPigs: '',
      checklist: '',
      feedSuppliers: ''
    };
    let hasError = false;

    if (amountOfPigs <= 0) {
      newErrors.amountOfPigs = t('f-pre-delivery-error-amount-of-pigs');
      hasError = true;
    }

    const unanswered = foodChainRef.current?.getUnansweredQuestions() ?? [];

    if (unanswered.length > 0) {
      newErrors.checklist = t('f-pre-delivery-error-checklist');
      hasError = true;
    }

    const validFeedSuppliers = feedSuppliers.slice(1).filter((s) => s.ubn && s.name && s.address && s.country);

    if (validFeedSuppliers.length === 0) {
      newErrors.feedSuppliers = t('f-pre-delivery-error-feed-suppliers');
      hasError = true;
    }

    setErrors(newErrors);
    return !hasError;
  };

  const buildCommand = (): HealthSystemDomainSlaughterPreparationsSlaughterPreDeliveryCommand => ({
    amount: amountOfPigs,
    answers: foodChainRef.current?.getMappedAnswers() ?? {},
    feedSuppliers: feedSuppliers.slice(1),
    pigletsSuppliers: pigSuppliers.slice(1),
    veterinaryId: veterinaryInfo?.id ?? null,
    createdAt: currentDate.toISOString()
  });

  const handleSave = async () => {
    try {
      const dto = buildCommand();

      await apiService.api.slaughterPreparation.slaughterPreparationSavePreDelivery(dto);

      setFeedSuppliers([emptySupplier]);
      setPigSuppliers([emptySupplier]);
      setAmountOfPigs(0);
      setEligibleAnimalsCount(null);
      setAnswers({} as Record<string, boolean | null>);
      setErrors({ amountOfPigs: '', checklist: '', feedSuppliers: '' });
      setWarnings({});
      getSummaryInfo();
      setIsCompleted(false);

      // Trigger callback to switch to history tab
      onSaveSuccess?.();
    } catch (err) {
      console.error('Save failed:', err);
    }
  };

  return (
    <Paper sx={{ p: 4 }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <Box sx={{ gap: 1, alignItems: 'baseline', display: 'flex', mb: 2 }}>
            <Typography variant="h5">{t('f-pre-delivery-title')}</Typography>
            <Typography variant="h6" color="text.secondary">
              {currentDate.toLocaleDateString()}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      <Grid container spacing={3}>
        <Grid container>
          {!isCompleted && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ alignItems: 'flex-start', flexDirection: 'column', gap: 1, display: 'flex' }}>
                <Box sx={{ gap: 1, alignItems: 'center', display: 'flex' }}>
                  <Typography>{t('f-pre-delivery-available-pigs')}</Typography>
                  <Typography variant="h6" color="primary">
                    {eligibleAnimalsCount ?? '-'}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {t('f-pre-delivery-note-animals-info')}
                </Typography>
              </Box>
            </Grid>
          )}
          <Grid container size={{ xs: 12 }} sx={{ mb: 2 }}>
            <Box sx={{ gap: 1, alignItems: 'center', display: 'flex' }}>
              <Typography>{t('f-pre-delivery-pre-delivery-amount')}</Typography>
              {!isCompleted ? (
                <TextField
                  label={t('f-pre-delivery-amount-of-pigs')}
                  error={!!errors.amountOfPigs}
                  helperText={
                    errors.amountOfPigs ? (
                      errors.amountOfPigs
                    ) : warnings.amountOfPigs ? (
                      <span style={{ color: '#ed6c02' }}>{warnings.amountOfPigs}</span>
                    ) : (
                      ''
                    )
                  }
                  type="number"
                  fullWidth
                  value={amountOfPigs === 0 ? '' : amountOfPigs}
                  onChange={(e) => handleAmountChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAmountChange(String(amountOfPigs));
                    }
                  }}
                />
              ) : (
                <Typography variant="h6" color="primary">
                  {amountOfPigs ?? '-'}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {t('f-pre-delivery-food-chain-info')}
          </Typography>
          {errors.checklist && (
            <Typography color="error" variant="body2">
              {errors.checklist}
            </Typography>
          )}
          <FoodChainQuestion
            ref={foodChainRef}
            answers={answers}
            onChange={(id, value) => setAnswers((prev) => ({ ...prev, [id]: value }))}
            isCompleted={isCompleted}
          />
          <Divider sx={{ my: 2 }} />
        </Grid>

        <Grid container size={{ xs: 12 }}>
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {t('f-pre-delivery-veterinary-info')}
            </Typography>
          </Grid>

          <Grid container size={{ xs: 12 }}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {t('f-pre-delivery-veterinary-name')}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body1" color="text.secondary">
                {t('f-pre-delivery-veterinary-address')}
              </Typography>
            </Grid>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2">{veterinaryInfo?.name ?? '-'}</Typography>
          </Grid>
          <Grid size={{ xs: 6, sm: 3 }}>
            <Typography variant="body2">{veterinaryInfo?.address ?? '-'}</Typography>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Divider sx={{ my: 2 }} />
          </Grid>
        </Grid>

        <Grid size={{ xs: 12 }}>
          {(!isCompleted || pigSuppliers.slice(1).length > 0) && (
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
              {t('f-pre-delivery-pig-suppliers')}
            </Typography>
          )}
          {pigSuppliers.slice(1).length > 0 && (
            <>
              {renderSuppliersTable(pigSuppliers.slice(1), (index) => removePigSupplier(index + 1))}
              <Grid size={{ xs: 12 }}>
                <Divider sx={{ my: 2 }} />
              </Grid>
            </>
          )}
          {!isCompleted && (
            <Grid container spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
              <Grid size={{ xs: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={pigSuppliers[0].ubn}
                  onChange={(e) => handlePigSupplierChange(0, 'ubn', e.target.value)}
                  label={t('f-pre-delivery-ubn')}
                />
              </Grid>

              <Grid size={{ xs: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={pigSuppliers[0].name}
                  onChange={(e) => handlePigSupplierChange(0, 'name', e.target.value)}
                  label={t('f-pre-delivery-company-name')}
                />
              </Grid>

              <Grid size={{ xs: 4 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={pigSuppliers[0].address}
                  onChange={(e) => handlePigSupplierChange(0, 'address', e.target.value)}
                  label={t('f-pre-delivery-address')}
                />
              </Grid>

              <Grid size={{ xs: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={pigSuppliers[0].country}
                  onChange={(e) => handlePigSupplierChange(0, 'country', e.target.value)}
                  label={t('f-country')}
                />
              </Grid>

              <Grid size={{ xs: 2 }} sx={{ justifyContent: 'flex-end', display: 'flex' }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    const newSupplier = pigSuppliers[0];
                    if (!newSupplier.ubn || !newSupplier.name || !newSupplier.address || !newSupplier.country) return;

                    setPigSuppliers((prev) => [emptySupplier, ...prev.slice(1), newSupplier]);
                  }}
                >
                  {t('f-pre-delivery-add-pig-supplier')}
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            {t('f-pre-delivery-feed-suppliers')}
          </Typography>
          {errors.feedSuppliers && (
            <Typography color="error" variant="body2">
              {errors.feedSuppliers}
            </Typography>
          )}
          {renderSuppliersTable(feedSuppliers.slice(1), (index) => removeFeedSupplier(index + 1))}

          <Divider sx={{ my: 2 }} />
          {!isCompleted && <FeedSupplierAutocomplete onSelect={handleFeedSupplierSelect} />}

          <Divider sx={{ my: 2 }} />
          <Grid size={{ xs: 12 }}>
            {isCompleted ? (
              <Box sx={{ gap: 2, display: 'flex' }}>
                <Button variant="outlined" onClick={() => setIsCompleted(false)}>
                  {t('f-go-back')}
                </Button>
                <Button variant="contained" color="primary" onClick={handleSave}>
                  {t('f-save')}
                </Button>
              </Box>
            ) : (
              <Button variant="outlined" onClick={() => validateForm() && setIsCompleted(true)}>
                {t('f-preview')}
              </Button>
            )}
          </Grid>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default PreDeliveryDeclarationForm;
